"use client";
import { createContext, useState, ReactNode, useEffect, useRef } from "react";
import LocalStorageUtils from "@/utils/localStorageUtils";
import { getPersistedFormValues } from "@/hooks/usePersistForm";
import { useSchema } from "../FormSchemaContext";
import usePrepareFormData from "@/hooks/usePrepareFormData";

interface FormContextProps {
  formData: any;
  setFormData: (data: any) => void;
  formDetails: any;
  setFormDetails: (data: any) => void;
  step: number;
  setStep: (step: number) => void;
  completedSteps: any;
  setCompletedSteps: (data: any) => void;
  validatedForm: any;
  setValidatedForm: (data: any) => void;
  loadingFromApi: boolean;
  setLoadingFromApi: (data: any) => void;
  // The next parameters are used to manage the service selection and proof of travel including:
  // - selectedMethod: The service selected by the user
  // - servicesDetails: The active services details related to the form (retrieved from the API)
  // - proofOfTravelValue: The value of the proof of travel field
  // - proofOfTravelError: To manage error related to the proof of travel field
  // - proofOfTravelRef: To scroll to proofOfTravel when the error is shown
  selectedMethod: string;
  setSelectedMethod: (method: string) => void;
  servicesDetails: any;
  setServicesDetails: (data: any) => void;
  proofOfTravelValue: string;
  setProofOfTravelValue: (value: string) => void;
  proofOfTravelError: boolean;
  setProofOfTravelError: (error: boolean) => void;
  proofOfTravelRef: React.RefObject<HTMLDivElement>;
}

export const FormContext = createContext<FormContextProps | undefined>(undefined);

export const FormProvider = ({ children }: { children: ReactNode }) => {
  const { pdfId } = useSchema();
  const {
    loading,
    orderFromApi,
    formHasLoadedData,
    formDetails: formDetailsFromApi,
    servicesDetails: servicesDetailsFromApi,
  } = usePrepareFormData({ pdfId });

  const formName = formDetailsFromApi?.pdf.name;
  // Retrieve data from localStorage or initialize with default values
  const sharedData = JSON.parse(LocalStorageUtils.getItem("FORM_DATA") || "{}");
  const specificData = JSON.parse(LocalStorageUtils.getItem(`FORM_DATA_${formName}`) || "{}");

  const [formData, setFormData] = useState<Record<string, any>>({ ...sharedData, ...specificData });

  const [formDetails, setFormDetails] = useState(() => {
    const savedData = LocalStorageUtils.getItem("FORM_DETAILS") || {};
    return savedData;
  });

  const [servicesDetails, setServicesDetails] = useState<Record<string, any>[]>(() => {
    const savedData = LocalStorageUtils.getItem("SERVICES_DETAILS");
    return savedData || [];
  });

  const [proofOfTravelValue, setProofOfTravelValue] = useState<string>("");
  const [proofOfTravelError, setProofOfTravelError] = useState<boolean>(false);
  const proofOfTravelRef = useRef<HTMLDivElement>(null);

  const [loadingFromApi, setLoadingFromApi] = useState<boolean>(true);

  const [completedSteps, setCompletedSteps] = useState(() => {
    const savedData = LocalStorageUtils.getItem("COMPLETED_STEPS") || [];
    return savedData;
  });

  const [validatedForm, setValidatedForm] = useState<boolean>(false);

  const [step, setStep] = useState(() => {
    const savedStep = LocalStorageUtils.getItem("STEP") || 0;
    return savedStep;
  });

  const [selectedMethod, setSelectedMethod] = useState<string>(() => {
    const savedMethod = LocalStorageUtils.getItem("SELECTED_METHOD");
    return savedMethod || "form";
  });

  useEffect(() => {
    setFormData({ ...formData, ...orderFromApi });
    setValidatedForm(formHasLoadedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFromApi]);

  useEffect(() => {
    if (formName) {
      const savedName: string | null = LocalStorageUtils.getItem("ACTIVE_FORM_NAME");
      if (savedName !== formName) {
        const reloadedFormData = getPersistedFormValues(formName);
        setStep(0);
        setCompletedSteps([]);
        setFormData(reloadedFormData);
        LocalStorageUtils.setItem("ACTIVE_FORM_NAME", formName);
      }
    }
  }, [formName]);

  useEffect(() => {
    if (formDetailsFromApi) setFormDetails(formDetailsFromApi);
  }, [formDetailsFromApi]);

  useEffect(() => {
    if (servicesDetailsFromApi) setServicesDetails(servicesDetailsFromApi);
  }, [servicesDetailsFromApi]);

  useEffect(() => {
    setLoadingFromApi(loading);
  }, [loading]);

  useEffect(() => {
    LocalStorageUtils.setItem("STEP", step);
  }, [step]);

  useEffect(() => {
    LocalStorageUtils.setItem("COMPLETED_STEPS", completedSteps);
  }, [completedSteps]);

  useEffect(() => {
    if (servicesDetails) {
      LocalStorageUtils.setItem("SERVICES_DETAILS", servicesDetails);
    }
  }, [servicesDetails]);

  useEffect(() => {
    LocalStorageUtils.setItem("SELECTED_METHOD", selectedMethod);
  }, [selectedMethod]);

  return (
    <FormContext.Provider
      value={{
        formData,
        setFormData,
        formDetails,
        setFormDetails,
        step,
        setStep,
        completedSteps,
        setCompletedSteps,
        validatedForm,
        setValidatedForm,
        loadingFromApi,
        setLoadingFromApi,
        selectedMethod,
        setSelectedMethod,
        servicesDetails,
        setServicesDetails,
        proofOfTravelValue,
        setProofOfTravelValue,
        proofOfTravelError,
        setProofOfTravelError,
        proofOfTravelRef,
      }}>
      {children}
    </FormContext.Provider>
  );
};
